// src/components/Form4ScreenerResult.tsx
import React, { useState } from "react";
import type { Form4ScreenerResponse, Form4ScreenerRow, Form4DetailRow } from "../api/myTypes";

interface Form4ScreenerResultProps {
  data: Form4ScreenerResponse;
}

function formatMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function formatNum(n: number | null | undefined): string {
  if (n == null) return "—";
  return String(n);
}

const Form4ScreenerResult: React.FC<Form4ScreenerResultProps> = ({ data }) => {
  const [expandedTicker, setExpandedTicker] = useState<string | null>(null);
  const { rows, params, generated_at, errors } = data;

  return (
    <div className="form4-screener-result" style={{ marginTop: "1rem" }}>
      <h3 style={{ fontWeight: "700", fontSize: "1.125rem", color: "var(--text-main)", marginBottom: "0.5rem" }}>
        Insider Form 4 — Top {params.max_tickers} by Net Buy $ (7D)
      </h3>
      <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
        Generated {new Date(generated_at).toLocaleString()} · Recent window: {params.days_recent}D · Aggregate: {params.days_aggregate}D · Max price: ${params.max_price}
      </p>
      {errors.length > 0 && (
        <div className="alert alert-error" style={{ marginBottom: "1rem" }}>
          {errors.join(" ")}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table className="form4-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid rgba(148,163,184,0.3)" }}>
              <th style={{ textAlign: "left", padding: "8px 6px" }}>Ticker</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Price</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Form4 (2D)</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Form4 (7D)</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Insiders</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Buy$ 7D</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Sell$ 7D</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Net$ 7D</th>
              <th style={{ textAlign: "right", padding: "8px 6px" }}>Largest Buy$</th>
              <th style={{ textAlign: "left", padding: "8px 6px" }}>Latest Filing</th>
              <th style={{ width: 36, padding: "8px 4px" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: Form4ScreenerRow) => {
              const isExpanded = expandedTicker === row.ticker;
              return (
                <React.Fragment key={row.ticker}>
                  <tr
                    className="form4-row"
                    style={{
                      borderBottom: "1px solid rgba(148,163,184,0.15)",
                      cursor: "pointer",
                      backgroundColor: isExpanded ? "rgba(59, 130, 246, 0.08)" : undefined,
                    }}
                    onClick={() => setExpandedTicker(isExpanded ? null : row.ticker)}
                  >
                    <td style={{ padding: "8px 6px", fontWeight: "600" }}>{row.ticker}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px" }}>{row.price != null ? `$${row.price.toFixed(2)}` : "—"}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px" }}>{formatNum(row.filings_recent_count)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px" }}>{formatNum(row.filings_7d_count)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px" }}>{formatNum(row.unique_insiders_7d)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px", color: "#22c55e" }}>{formatMoney(row.buys_7d_value)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px", color: "#f87171" }}>{formatMoney(row.sells_7d_value)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px", fontWeight: "600" }}>{formatMoney(row.net_value_7d)}</td>
                    <td style={{ textAlign: "right", padding: "8px 6px" }}>{formatMoney(row.largest_buy_7d_value)}</td>
                    <td style={{ padding: "8px 6px" }}>{row.latest_filing_date || "—"}</td>
                    <td style={{ padding: "8px 4px" }}>{isExpanded ? "▼" : "▶"}</td>
                  </tr>
                  {isExpanded && row.details && row.details.length > 0 && (
                    <tr style={{ backgroundColor: "rgba(15,23,42,0.5)" }}>
                      <td colSpan={11} style={{ padding: "12px 8px", borderBottom: "1px solid rgba(148,163,184,0.2)" }}>
                        <div style={{ fontSize: "0.8rem" }}>
                          <strong style={{ color: "var(--text-muted)" }}>Form 4 details — {row.ticker} ({row.name})</strong>
                          {row.edgar_company_url && (
                            <span style={{ marginLeft: "8px" }}>
                              <a href={row.edgar_company_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-strong)" }}>
                                SEC Filings
                              </a>
                            </span>
                          )}
                        </div>
                        <table style={{ width: "100%", marginTop: "8px", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.25)" }}>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Insider</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Role</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Txn Date</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Filing Date</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Code</th>
                              <th style={{ textAlign: "right", padding: "6px 4px" }}>Shares</th>
                              <th style={{ textAlign: "right", padding: "6px 4px" }}>Price</th>
                              <th style={{ textAlign: "right", padding: "6px 4px" }}>Value</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Owned After</th>
                              <th style={{ textAlign: "left", padding: "6px 4px" }}>Filing Link</th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.details.map((d: Form4DetailRow, idx: number) => (
                              <tr key={idx} style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                                <td style={{ padding: "6px 4px" }}>{d.insider_name}</td>
                                <td style={{ padding: "6px 4px" }}>{d.insider_role}</td>
                                <td style={{ padding: "6px 4px" }}>{d.transaction_date}</td>
                                <td style={{ padding: "6px 4px" }}>{d.filing_date}</td>
                                <td style={{ padding: "6px 4px" }}>{d.txn_code}</td>
                                <td style={{ textAlign: "right", padding: "6px 4px" }}>{formatNum(d.shares)}</td>
                                <td style={{ textAlign: "right", padding: "6px 4px" }}>{d.price != null ? `$${d.price.toFixed(2)}` : "—"}</td>
                                <td style={{ textAlign: "right", padding: "6px 4px" }}>{formatMoney(d.value)}</td>
                                <td style={{ padding: "6px 4px" }}>{d.shares_owned_after || "—"}</td>
                                <td style={{ padding: "6px 4px" }}>
                                  {d.filing_url ? (
                                    <a href={d.filing_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-strong)" }}>
                                      Link
                                    </a>
                                  ) : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && !errors.length && (
        <p style={{ color: "var(--text-muted)", marginTop: "1rem" }}>No tickers with recent Form 4 filings in the selected window (or none under max price).</p>
      )}
    </div>
  );
};

export default Form4ScreenerResult;
